import { Badge, Box, Divider, Grid, Heading, Text, useColorModeValue } from "@chakra-ui/react";
import { TcmEncounter } from "../../../api/tcm-encounter";
import React from "react";

const icd10TestInterpretationPrefixes = ["R87", "R88", "R89", "R90", "R91", "R92", "R93", "R94"];

type Icd10CodeType =
  | "external_cause"
  | "administrative"
  | "symptom"
  | "test_interpretation"
  | "diagnosis";

/**
 * See documentation on ICD10 codes by letter: https://www.icd10data.com/ICD10CM/Codes/
 */
function getIcd10CodeType(code: string): Icd10CodeType {
  const firstLetter = code.charAt(0);

  if (firstLetter === "Z") {
    return "administrative";
  }

  if (["V", "W", "X", "Y"].includes(firstLetter)) {
    return "external_cause";
  }

  if (firstLetter === "R") {
    if (icd10TestInterpretationPrefixes.some(prefix => code.startsWith(prefix))) {
      return "test_interpretation";
    }
    if (code.startsWith("R69")) {
      return "administrative";
    }
    return "symptom";
  }

  return "diagnosis";
}

/**
 * Component that displays a coding section with heading and grid of condition codes and descriptions
 */
function CodingSection({
  title,
  conditions,
  badgeColor,
}: {
  title: string;
  conditions: Array<{ coding: Array<{ code?: string; display?: string }> }>;
  badgeColor: string;
}) {
  const textColor = useColorModeValue("gray.800", "white");
  const subHeadingColor = useColorModeValue("gray.600", "gray.400");

  return (
    <Box mb={5}>
      <Heading size="sm" color={subHeadingColor} mb={3}>
        {title}
      </Heading>
      <Grid templateColumns="auto 1fr" alignItems="center" gap={1} columnGap={4}>
        {conditions.map((item, index: number) => {
          const primaryCoding = item.coding[0];
          const code = primaryCoding?.code || "-";
          const display = primaryCoding?.display || "Unknown condition";

          return (
            <React.Fragment key={`code-${index}`}>
              <Badge colorScheme={badgeColor} w="fit-content" borderRadius="md">
                {code}
              </Badge>
              <Text fontSize="md" color={textColor}>
                {display}
              </Text>
            </React.Fragment>
          );
        })}
      </Grid>
    </Box>
  );
}

/**
 * Clinical information display component that shows diagnostic codes and descriptions
 * grouped by ICD-10 code type in a boxed layout similar to encounter notes
 */
export function ClinicalInformationDisplay({ encounter }: { encounter: TcmEncounter }) {
  const borderColor = useColorModeValue("gray.300", "gray.600");

  // Extract clinical information from encounter
  const conditions = encounter.clinicalInformation?.condition;

  // Check if clinical information exists and is an array
  const hasConditions = conditions && Array.isArray(conditions);

  // Filter out items that don't have coding information
  const validConditions = hasConditions
    ? conditions.filter(
        item =>
          item && typeof item === "object" && Array.isArray(item.coding) && item.coding.length > 0
      )
    : [];

  const hasValidConditions = validConditions.length > 0;

  const diagnosisConditions = validConditions
    .filter(item => getIcd10CodeType(item.coding?.[0]?.code || "") === "diagnosis")
    .filter(item => item.coding && item.coding.length > 0);
  const otherConditions = validConditions
    .filter(item => getIcd10CodeType(item.coding?.[0]?.code || "") !== "diagnosis")
    .filter(item => item.coding && item.coding.length > 0);

  return (
    <Box mb={6}>
      <Heading size="md" mb={4}>
        Encounter Information
      </Heading>

      {!hasValidConditions ? (
        <Text fontSize="md" fontStyle="italic">
          No information found for encounter.
        </Text>
      ) : (
        <>
          <CodingSection
            title={"Diagnosis Codes"}
            conditions={diagnosisConditions}
            badgeColor={"blue"}
          />
          <Divider my={4} borderColor={borderColor} />
          <CodingSection title={"Other Codes"} conditions={otherConditions} badgeColor={"gray"} />
        </>
      )}
    </Box>
  );
}
